<script lang="ts" setup>
import { computed, ref } from 'vue';
import ResourceItem from './ResourceItem.vue';
import { useResourceEvents } from '/@features/resourceEvent/useResourceEvents';
import DfCalendar from '/@elements/DfCalendar/DfCalendar.vue';
import { loadResourceFilter, loadResources } from '/@stores/resources';
import { Resource, ResourceFilter, ResourceType } from '/@types/resources';

const resources = ref<Array<Resource>>([]);
const filters = ref<Array<ResourceFilter>>([]);

const { isLoading, selectedDate, events, groupSearch, selectedGroupUsers, dateChange } =
  useResourceEvents({ byUser: false });

const selectedUserGuid = ref<string | null>(null);

const filteredEvents = computed(() => {
  return events.value.filter((event) => {
    if (event.resourceType !== 'user') return false;
    if (selectedUserGuid.value && event.resourceGuid !== selectedUserGuid.value) return false;
    return true;
  });
});

const mappedResources = computed(() => {
  const filteredResources =
    selectedGroupUsers.value.length > 0
      ? resources.value.filter((res) => selectedGroupUsers.value.includes(res.guid))
      : resources.value;
  return new Map(filteredResources.map((res) => [res.guid, { name: res.name }]));
});

const mappedGroups = computed(
  () => new Map(filters.value.map((res) => [res.id, { name: res.name }])),
);

function initialize() {
  Promise.all([
    loadResources({ type: 'user' }).then((data) => (resources.value = data || [])),
    loadResourceFilter().then((res) => (filters.value = res || [])),
  ]);
}

initialize();
</script>

<template>
  <div class="page page--wide">
    <header>
      <h1>Alle avtaler</h1>

      <nav class="header-nav">
        <div class="header-nav__select">
          <df-select3
            elevate
            label="Gruppe"
            v-model="groupSearch"
            :entries="mappedGroups"
            display="name"
          >
          </df-select3>

          <df-button v-if="groupSearch" v-on:click="groupSearch = null" elevate>
            <template v-slot:icon>
              <df-icon code="f00d" />
            </template>
          </df-button>
        </div>

        <div class="header-nav__select">
          <df-select3
            elevate
            label="Bruker"
            v-model="selectedUserGuid"
            :entries="mappedResources"
            display="name"
          >
          </df-select3>

          <df-button v-if="selectedUserGuid" v-on:click="selectedUserGuid = null" elevate>
            <template v-slot:icon>
              <df-icon code="f00d" />
            </template>
          </df-button>
        </div>
      </nav>
    </header>

    <df-loading v-if="isLoading" />

    <df-calendar
      class="page__calendar"
      view="dfListDay"
      buttons="dfListWeek,dfListDay"
      display-user
      :events="filteredEvents"
      :resources="resources"
      v-on:date-change="dateChange($event)"
      v-on:day-click="selectedDate = $event"
    >
      <template v-slot:event="{ event, close }">
        <ResourceItem :event="event" :close="close" :day="selectedDate" />
      </template>
    </df-calendar>
  </div>
</template>

<style scoped>
.page {
  padding-top: calc(var(--gap-md) + env(safe-area-inset-top));
  padding-left: calc(var(--gap-md) + env(safe-area-inset-left));
  padding-right: calc(var(--gap-md) + env(safe-area-inset-right));
  padding-bottom: var(--gap-md);
  display: grid;
  grid-template-rows: max-content 1fr;
}

.page,
.page__calendar {
  max-width: 120ch;
  margin: 0 auto;
  min-height: 100%;
}

header {
  display: flex;
  flex-direction: column;
  gap: var(--gap-lg);
}

.header-nav {
  display: flex;
  flex-direction: column;
  gap: var(--gap-md);
}

.header-nav__select {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  grid-template-columns: 1fr;
  gap: var(--gap-md);
}
</style>
